'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import TeamLogo from '@/components/ui/TeamLogo';
import StatCard from '@/components/ui/StatCard';
import dynamic from 'next/dynamic';

const TeamXGoalsChart = dynamic(() => import('./TeamXGoalsChart'), { ssr: false });
const TeamCorsiChart = dynamic(() => import('./TeamCorsiChart'), { ssr: false });
const TeamSplitsChart = dynamic(() => import('./TeamSplitsChart'), { ssr: false });

const TEAMS = [
  { id: 'team-fla', name: 'Florida Panthers', abbr: 'FLA', color: '#C8102E', division: 'Atlantique', conference: 'Est' },
  { id: 'team-tor', name: 'Toronto Maple Leafs', abbr: 'TOR', color: '#00205B', division: 'Atlantique', conference: 'Est' },
  { id: 'team-bos', name: 'Boston Bruins', abbr: 'BOS', color: '#FCB514', division: 'Atlantique', conference: 'Est' },
  { id: 'team-edm', name: 'Edmonton Oilers', abbr: 'EDM', color: '#FF4C00', division: 'Pacifique', conference: 'Ouest' },
  { id: 'team-col', name: 'Colorado Avalanche', abbr: 'COL', color: '#6F263D', division: 'Centrale', conference: 'Ouest' },
  { id: 'team-vgk', name: 'Vegas Golden Knights', abbr: 'VGK', color: '#B4975A', division: 'Pacifique', conference: 'Ouest' },
  { id: 'team-nyr', name: 'New York Rangers', abbr: 'NYR', color: '#0038A8', division: 'Métropolitaine', conference: 'Est' },
  { id: 'team-dal', name: 'Dallas Stars', abbr: 'DAL', color: '#006847', division: 'Centrale', conference: 'Ouest' },
  { id: 'team-mtl', name: 'Montréal Canadiens', abbr: 'MTL', color: '#AF1E2D', division: 'Atlantique', conference: 'Est' },
];

const TEAM_DATA: Record<string, {
  record: string; pts: number; gp: number;
  xGF: number; xGA: number; corsi: number;
  pp: number; pk: number; sv: number; gaa: number;
  homeRecord: string; awayRecord: string;
  l10: string; divRank: number;
  goalies: Array<{ id: string; name: string; gp: number; sv: number; gaa: number; record: string }>;
  roster: Array<{ id: string; name: string; pos: string; goals: number; assists: number; points: number; status: 'Active' | 'Blessé' | 'Questionable' }>;
}> = {
  'team-fla': {
    record: '54-21-7', pts: 115, gp: 82,
    xGF: 3.61, xGA: 2.48, corsi: 54.2,
    pp: 24.3, pk: 83.2, sv: 0.918, gaa: 2.41,
    homeRecord: '29-10-2', awayRecord: '25-11-5',
    l10: '7-2-1', divRank: 1,
    goalies: [
      { id: 'g-bob', name: 'S. Bobrovsky', gp: 58, sv: 0.921, gaa: 2.38, record: '38-14-4' },
      { id: 'g-kni', name: 'S. Knight', gp: 24, sv: 0.911, gaa: 2.51, record: '16-7-3' },
    ],
    roster: [
      { id: 'p-benn', name: 'S. Bennett', pos: 'C', goals: 38, assists: 44, points: 82, status: 'Active' },
      { id: 'p-rein', name: 'M. Reinhart', pos: 'C', goals: 52, assists: 61, points: 113, status: 'Active' },
      { id: 'p-barkov', name: 'A. Barkov', pos: 'C', goals: 31, assists: 58, points: 89, status: 'Active' },
      { id: 'p-tkachuk', name: 'M. Tkachuk', pos: 'LW', goals: 41, assists: 52, points: 93, status: 'Active' },
      { id: 'p-montour', name: 'B. Montour', pos: 'D', goals: 14, assists: 41, points: 55, status: 'Blessé' },
    ],
  },
  'team-tor': {
    record: '51-24-7', pts: 109, gp: 82,
    xGF: 3.48, xGA: 2.71, corsi: 52.8,
    pp: 22.1, pk: 80.1, sv: 0.908, gaa: 2.73,
    homeRecord: '28-11-2', awayRecord: '23-13-5',
    l10: '5-4-1', divRank: 2,
    goalies: [
      { id: 'g-woll', name: 'J. Woll', gp: 52, sv: 0.912, gaa: 2.61, record: '32-14-4' },
      { id: 'g-stos', name: 'M. Stolarz', gp: 30, sv: 0.901, gaa: 2.94, record: '19-10-3' },
    ],
    roster: [
      { id: 'p-matt', name: 'A. Matthews', pos: 'C', goals: 48, assists: 44, points: 92, status: 'Questionable' },
      { id: 'p-marner', name: 'M. Marner', pos: 'RW', goals: 31, assists: 73, points: 104, status: 'Active' },
      { id: 'p-nylander', name: 'W. Nylander', pos: 'RW', goals: 44, assists: 51, points: 95, status: 'Active' },
      { id: 'p-tavares', name: 'J. Tavares', pos: 'C', goals: 24, assists: 39, points: 63, status: 'Active' },
      { id: 'p-rielly', name: 'M. Rielly', pos: 'D', goals: 8, assists: 48, points: 56, status: 'Active' },
    ],
  },
  'team-edm': {
    record: '53-22-7', pts: 113, gp: 82,
    xGF: 3.88, xGA: 2.62, corsi: 56.1,
    pp: 28.7, pk: 85.1, sv: 0.914, gaa: 2.55,
    homeRecord: '30-9-2', awayRecord: '23-13-5',
    l10: '8-1-1', divRank: 1,
    goalies: [
      { id: 'g-skin', name: 'S. Skinner', gp: 60, sv: 0.917, gaa: 2.49, record: '40-13-5' },
      { id: 'g-camp', name: 'C. Campbell', gp: 22, sv: 0.904, gaa: 2.78, record: '13-9-2' },
    ],
    roster: [
      { id: 'p-mcd', name: 'C. McDavid', pos: 'C', goals: 58, assists: 94, points: 152, status: 'Active' },
      { id: 'p-draisaitl', name: 'L. Draisaitl', pos: 'C', goals: 51, assists: 72, points: 123, status: 'Active' },
      { id: 'p-nugent', name: 'R. Nugent-Hopkins', pos: 'LW', goals: 28, assists: 44, points: 72, status: 'Active' },
      { id: 'p-ekholm', name: 'M. Ekholm', pos: 'D', goals: 11, assists: 42, points: 53, status: 'Active' },
      { id: 'p-bouchard', name: 'E. Bouchard', pos: 'D', goals: 18, assists: 58, points: 76, status: 'Active' },
    ],
  },
};

const FALLBACK_DATA = TEAM_DATA['team-fla'];

const STATUS_CONFIG = {
  Active: 'bg-positive-subtle text-positive border-positive/20',
  Blessé: 'bg-negative-subtle text-negative border-negative/20',
  Questionable: 'bg-warning-subtle text-warning border-warning/20',
};

export default function TeamAnalysisClient() {
  const [selectedTeamId, setSelectedTeamId] = useState('team-fla');
  const team = TEAMS.find((t) => t.id === selectedTeamId) ?? TEAMS[0];
  const data = TEAM_DATA[selectedTeamId] ?? FALLBACK_DATA;
  const [activeTab, setActiveTab] = useState<'overview' | 'charts' | 'roster' | 'goalies'>('overview');

  return (
    <div className="space-y-4">
      {/* Team selector */}
      <div className="card p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Icon name="MagnifyingGlassIcon" size={16} className="text-muted-foreground" />
          <div className="flex flex-wrap gap-2">
            {TEAMS.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTeamId(t.id)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-all duration-150 border ${
                  selectedTeamId === t.id
                    ? 'border-primary/40 bg-primary/10 text-primary' :'border-border bg-muted text-muted-foreground hover:text-foreground hover:border-border/80'
                }`}
              >
                <span
                  className="w-2 h-2 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: t.color }}
                />
                {t.abbr}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Team header */}
      <div className="card p-5 border-l-4 flex items-center justify-between gap-4 flex-wrap" style={{ borderLeftColor: team.color }}>
        <div className="flex items-center gap-4">
          <TeamLogo abbreviation={team.abbr} color={team.color} size="lg" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">{team.name}</h2>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              <span className="font-mono-data">{data.record}</span>
              <span className="text-border">·</span>
              <span className="font-mono-data text-primary font-semibold">{data.pts} pts</span>
              <span className="text-border">·</span>
              <span>{team.division} — {team.conference}</span>
              <span className="text-border">·</span>
              <span>#{data.divRank} division</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono-data">L10: </span>
          <span className="font-bold font-mono-data text-foreground">{data.l10}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(['overview', 'charts', 'roster', 'goalies'] as const).map((tab) => {
          const labels = { overview: 'Vue générale', charts: 'Graphiques', roster: 'Effectif', goalies: 'Gardiens' };
          return (
            <button
              key={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium transition-all duration-150 border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {labels[tab]}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
          <StatCard label="xGoals Pour" value={data.xGF.toString()} subValue="Par match — offensif" trend="up" trendValue={`+${(data.xGF - 3.1).toFixed(2)} vs moy.`} icon="BoltIcon" variant={data.xGF > 3.4 ? 'positive' : 'default'} />
          <StatCard label="xGoals Contre" value={data.xGA.toString()} subValue="Par match — défensif" trend={data.xGA < 2.7 ? 'up' : 'down'} trendValue={data.xGA < 2.7 ? 'Solide défense' : 'À surveiller'} icon="ShieldCheckIcon" variant={data.xGA > 2.8 ? 'warning' : 'positive'} />
          <StatCard label="Corsi%" value={`${data.corsi}%`} subValue="Possession tirs" trend={data.corsi > 52 ? 'up' : 'flat'} trendValue={data.corsi > 52 ? 'Dominant' : 'Équilibré'} icon="ChartBarIcon" />
          <StatCard label="Power Play%" value={`${data.pp}%`} subValue="Efficacité supériorité" trend={data.pp > 23 ? 'up' : 'flat'} trendValue={data.pp > 23 ? 'Élite' : 'Moyen'} icon="BoltIcon" variant={data.pp > 25 ? 'positive' : 'default'} />
          <StatCard label="Penalty Kill%" value={`${data.pk}%`} subValue="Efficacité infériorité" trend={data.pk > 82 ? 'up' : 'flat'} trendValue={data.pk > 82 ? 'Élite' : 'Correct'} icon="ShieldCheckIcon" />
          <StatCard label="Save%" value={data.sv.toString()} subValue="Performance gardiens" trend={data.sv > 0.913 ? 'up' : 'down'} trendValue={data.sv > 0.913 ? 'Excellent' : 'Moyen'} icon="UserCircleIcon" variant={data.sv < 0.905 ? 'alert' : 'default'} />
          <StatCard label="Buts c. / match" value={data.gaa.toString()} subValue="Goals Against Average" trend={data.gaa < 2.6 ? 'up' : 'down'} trendValue={data.gaa < 2.6 ? 'Solide' : 'À risque'} icon="ExclamationTriangleIcon" variant={data.gaa > 2.9 ? 'alert' : 'default'} />
          <div className="card stat-card-hover p-4">
            <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground mb-3">Dom. / Ext.</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Domicile</span>
                <span className="text-sm font-bold font-mono-data text-positive">{data.homeRecord}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Extérieur</span>
                <span className="text-sm font-bold font-mono-data text-foreground">{data.awayRecord}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'charts' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">xGoals — 20 derniers matchs</h3>
            <p className="text-xs text-muted-foreground mb-3">xGF (pour) vs xGA (contre)</p>
            <TeamXGoalsChart teamId={selectedTeamId} xGF={data.xGF} xGA={data.xGA} />
          </div>
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">Corsi% — Tendance saison</h3>
            <p className="text-xs text-muted-foreground mb-3">Possession tirs (rolling 5 matchs)</p>
            <TeamCorsiChart teamId={selectedTeamId} avgCorsi={data.corsi} />
          </div>
          <div className="card p-4 lg:col-span-2 xl:col-span-1">
            <h3 className="text-sm font-semibold text-foreground mb-1">Victoires Dom. vs Ext.</h3>
            <p className="text-xs text-muted-foreground mb-3">Répartition par lieu de match</p>
            <TeamSplitsChart homeRecord={data.homeRecord} awayRecord={data.awayRecord} />
          </div>
        </div>
      )}

      {activeTab === 'roster' && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Effectif — {team.name}</h3>
          </div>
          <table className="w-full">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                <th className="table-header">Joueur</th>
                <th className="table-header">Pos.</th>
                <th className="table-header text-right">B</th>
                <th className="table-header text-right">A</th>
                <th className="table-header text-right">PTS</th>
                <th className="table-header">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.roster.map((player) => (
                <tr key={player.id} className="border-b border-border row-hover">
                  <td className="table-cell font-medium text-foreground">{player.name}</td>
                  <td className="table-cell">
                    <span className="text-xs font-mono-data bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{player.pos}</span>
                  </td>
                  <td className="table-cell text-right font-mono-data text-foreground">{player.goals}</td>
                  <td className="table-cell text-right font-mono-data text-muted-foreground">{player.assists}</td>
                  <td className="table-cell text-right font-bold font-mono-data text-primary">{player.points}</td>
                  <td className="table-cell">
                    <span className={`status-badge border text-2xs ${STATUS_CONFIG[player.status]}`}>{player.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'goalies' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.goalies.map((goalie) => (
            <div key={goalie.id} className="card p-5 stat-card-hover">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <Icon name="UserCircleIcon" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{goalie.name}</p>
                  <p className="text-xs text-muted-foreground font-mono-data">{goalie.gp} matchs joués</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted rounded p-2.5 text-center">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">SV%</p>
                  <p className="text-base font-bold font-mono-data text-positive">{goalie.sv}</p>
                </div>
                <div className="bg-muted rounded p-2.5 text-center">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">GAA</p>
                  <p className="text-base font-bold font-mono-data text-foreground">{goalie.gaa}</p>
                </div>
                <div className="bg-muted rounded p-2.5 text-center">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Fiche</p>
                  <p className="text-sm font-bold font-mono-data text-foreground">{goalie.record}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}